import axios from 'axios';
import { useMutation, UseQueryOptions } from '@tanstack/react-query';
import { TalkRequest, TalkResponse } from '../types';
import { config } from '../config';

// Configurazione axios
const apiClient = axios.create({
  baseURL: config.API_URL,
  timeout: config.API_TIMEOUT,
  headers: config.DEFAULT_HEADERS,
});

const apiClientV1 = axios.create({
  baseURL: config.API_URL_V1,
  timeout: config.API_TIMEOUT,
  headers: config.DEFAULT_HEADERS,
});

// Interceptor per gestire errori
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
      throw new Error(`Impossibile connettersi al server. Verifica che il server backend sia in esecuzione su ${config.API_URL}`);
    }
    throw error;
  }
);

// Funzioni API base
export const talkOldAPI = async (data: TalkRequest): Promise<TalkResponse> => {
  const response = await apiClient.post('/talk_old', data);
  return response.data;
};

export const talkAPI = async (data: TalkRequest): Promise<TalkResponse> => {
  // Se c'è audio, convertilo in base64 come nel codice originale
  let requestData = { ...data };
  
  if (data.audio && data.audio instanceof Blob) {
    // Converti il Blob in base64
    const base64Audio = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(data.audio as Blob);
      reader.onloadend = () => {
        const result = reader.result as string;
        // For WAV, the prefix is data:audio/wav;base64,
        const base64 = result.split(',')[1]; // Rimuovi il prefisso data:audio/wav;base64,
        resolve(base64);
      };
      reader.onerror = reject;
    });
    
    requestData = {
      ...data,
      audio: base64Audio
    };
    
    console.log('Audio converted to base64 (WAV format), length:', base64Audio.length);
  }
  
  console.log('🚀 Sending request to /talk with data:', {
    ...requestData,
    audio: requestData.audio ? '[AUDIO_DATA]' : undefined
  });
  
  const response = await apiClient.post('/talk', requestData);
  console.log('📥 Raw response from /talk:', response);
  console.log('📥 Response data:', response.data);
  console.log('📥 Response chatId:', response.data?.chatId);
  
  return response.data;
};

export const loginAPI = async (data?: any): Promise<any> => {
  const response = await apiClient.post('/login', data || {});
  return response.data;
};

export const getChatMessagesAPI = async (chatId: string): Promise<any> => {
  const response = await apiClientV1.get(`/api/v1/chats/${chatId}`);
  return response.data;
};

/**
 * Hook for the Talk Old API mutation
 * Uses TanStack Query's useMutation for proper state management
 */
export const useTalkOld = () => {
  return useMutation({
    mutationFn: talkOldAPI,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 5000),
    onError: (error) => {
      console.error('❌ Talk Old API failed:', error);
    },
  });
};

/**
 * Hook for the Talk API mutation
 * Uses TanStack Query's useMutation for proper state management
 */
export const useTalk = () => {
  return useMutation({
    mutationFn: talkAPI,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 5000),
    onError: (error) => {
      console.error('❌ Talk API failed:', error);
    },
  });
};

/**
 * Hook for the Login API mutation
 * Uses TanStack Query's useMutation for proper state management
 */
export const useLogin = () => {
  return useMutation({
    mutationFn: loginAPI,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 5000),
    onError: (error) => {
      console.error('❌ Login API failed:', error);
    },
  });
};

/**
 * Query options for fetching chat messages
 * Use with useQuery: const { data } = useQuery(useChatMessages(chatId));
 */
export const useChatMessages = (
  chatId: string | null, 
  enabled: boolean = true
): UseQueryOptions<any, Error, any, string[]> => {
  return {
    queryKey: ['chatMessages', chatId ?? ''],
    queryFn: () => chatId ? getChatMessagesAPI(chatId) : Promise.resolve(null),
    enabled: enabled && !!chatId,
    refetchInterval: 3000, // Polling ogni 3 secondi
    retry: 2,
  };
};
